import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  applyNametag,
  applyStatTrak,
  equipSkin,
  getInventory,
  getMe,
  removeStatTrak,
  setWeaponPreference,
} from '../api/api';

const C = {
  red: '#E8192C',
  redDim: '#a4111d',
  redGlow: 'rgba(232,25,44,0.45)',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  steam: '#66c0f4',
  bg: '#0C0905',
  bgWarm: '#100B07',
  bgWarmer: '#15100B',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textVeryDim: '#5C554E',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const WEAPONS = [
  { id: 'ak47', name: 'AK-47', category: 'RIFLE' },
  { id: 'm4a4', name: 'M4A4', category: 'RIFLE' },
  { id: 'm4a1s', name: 'M4A1-S', category: 'RIFLE' },
  { id: 'sg553', name: 'SG 553', category: 'RIFLE' },
  { id: 'aug', name: 'AUG', category: 'RIFLE' },
  { id: 'famas', name: 'FAMAS', category: 'RIFLE' },
  { id: 'galil', name: 'Galil AR', category: 'RIFLE' },
  { id: 'awp', name: 'AWP', category: 'SNIPER' },
  { id: 'ssg08', name: 'SSG 08', category: 'SNIPER' },
  { id: 'scar20', name: 'SCAR-20', category: 'SNIPER' },
  { id: 'g3sg1', name: 'G3SG1', category: 'SNIPER' },
  { id: 'mp9', name: 'MP9', category: 'SMG' },
  { id: 'mp7', name: 'MP7', category: 'SMG' },
  { id: 'mp5sd', name: 'MP5-SD', category: 'SMG' },
  { id: 'ump45', name: 'UMP-45', category: 'SMG' },
  { id: 'p90', name: 'P90', category: 'SMG' },
  { id: 'bizon', name: 'PP-Bizon', category: 'SMG' },
  { id: 'mac10', name: 'MAC-10', category: 'SMG' },
  { id: 'xm1014', name: 'XM1014', category: 'HEAVY' },
  { id: 'mag7', name: 'MAG-7', category: 'HEAVY' },
  { id: 'sawedoff', name: 'Sawed-Off', category: 'HEAVY' },
  { id: 'nova', name: 'Nova', category: 'HEAVY' },
  { id: 'm249', name: 'M249', category: 'HEAVY' },
  { id: 'negev', name: 'Negev', category: 'HEAVY' },
  { id: 'deagle', name: 'Desert Eagle', category: 'PISTOL' },
  { id: 'usp', name: 'USP-S', category: 'PISTOL' },
  { id: 'glock', name: 'Glock-18', category: 'PISTOL' },
  { id: 'p2000', name: 'P2000', category: 'PISTOL' },
  { id: 'p250', name: 'P250', category: 'PISTOL' },
  { id: 'fiveseven', name: 'Five-SeveN', category: 'PISTOL' },
  { id: 'tec9', name: 'Tec-9', category: 'PISTOL' },
  { id: 'cz75', name: 'CZ75-Auto', category: 'PISTOL' },
  { id: 'r8', name: 'R8 Revolver', category: 'PISTOL' },
  { id: 'dualies', name: 'Dual Berettas', category: 'PISTOL' },
  { id: 'knife', name: 'Knife', category: 'GEAR' },
  { id: 'gloves', name: 'Gloves', category: 'GEAR' },
];

const CATEGORIES = ['RIFLE', 'SNIPER', 'SMG', 'HEAVY', 'PISTOL', 'GEAR'];
const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'equipped', label: 'Equipped' },
  { id: 'owned', label: 'Owned' },
];

const RARITY_COLORS = {
  consumer_grade: '#b0c3d9',
  industrial_grade: '#5e98d9',
  mil_spec: '#4b69ff',
  restricted: '#8847ff',
  classified: '#d32ce6',
  covert: '#eb4b4b',
  extraordinary: '#ffd700',
  contraband: '#e4ae39',
  'Consumer Grade': '#b0c3d9',
  'Industrial Grade': '#5e98d9',
  'Mil-Spec': '#4b69ff',
  Restricted: '#8847ff',
  Classified: '#d32ce6',
  Covert: '#eb4b4b',
  Extraordinary: '#ffd700',
  Contraband: '#e4ae39',
  Exclusive: '#ffd700',
};

const DEFAULT_WEAPON_IMAGES = {
  ak47: 'https://stash.clash.gg/img/weapons/512x384/ak-47.png',
  m4a4: 'https://stash.clash.gg/img/weapons/512x384/m4a4.png',
  m4a1s: 'https://stash.clash.gg/img/weapons/512x384/m4a1-s.png',
  awp: 'https://stash.clash.gg/img/weapons/512x384/awp.png',
  deagle: 'https://stash.clash.gg/img/weapons/512x384/desert_eagle.png',
  usp: 'https://stash.clash.gg/img/weapons/512x384/usp-s.png',
  glock: 'https://stash.clash.gg/img/weapons/512x384/glock-18.png',
  p250: 'https://stash.clash.gg/img/weapons/512x384/p250.png',
  fiveseven: 'https://stash.clash.gg/img/weapons/512x384/five-seven.png',
  tec9: 'https://stash.clash.gg/img/weapons/512x384/tec-9.png',
  p2000: 'https://stash.clash.gg/img/weapons/512x384/p2000.png',
  cz75: 'https://stash.clash.gg/img/weapons/512x384/cz75-auto.png',
  r8: 'https://stash.clash.gg/img/weapons/512x384/r8_revolver.png',
  dualies: 'https://stash.clash.gg/img/weapons/512x384/dual_berettas.png',
  sg553: 'https://stash.clash.gg/img/weapons/512x384/sg_553.png',
  aug: 'https://stash.clash.gg/img/weapons/512x384/aug.png',
  famas: 'https://stash.clash.gg/img/weapons/512x384/famas.png',
  galil: 'https://stash.clash.gg/img/weapons/512x384/galil_ar.png',
  ssg08: 'https://stash.clash.gg/img/weapons/512x384/ssg_08.png',
  scar20: 'https://stash.clash.gg/img/weapons/512x384/scar-20.png',
  g3sg1: 'https://stash.clash.gg/img/weapons/512x384/g3sg1.png',
  mp9: 'https://stash.clash.gg/img/weapons/512x384/mp9.png',
  mp7: 'https://stash.clash.gg/img/weapons/512x384/mp7.png',
  mp5sd: 'https://stash.clash.gg/img/weapons/512x384/mp5-sd.png',
  ump45: 'https://stash.clash.gg/img/weapons/512x384/ump-45.png',
  p90: 'https://stash.clash.gg/img/weapons/512x384/p90.png',
  bizon: 'https://stash.clash.gg/img/weapons/512x384/pp-bizon.png',
  mac10: 'https://stash.clash.gg/img/weapons/512x384/mac-10.png',
  xm1014: 'https://stash.clash.gg/img/weapons/512x384/xm1014.png',
  mag7: 'https://stash.clash.gg/img/weapons/512x384/mag-7.png',
  sawedoff: 'https://stash.clash.gg/img/weapons/512x384/sawed-off.png',
  nova: 'https://stash.clash.gg/img/weapons/512x384/nova.png',
  m249: 'https://stash.clash.gg/img/weapons/512x384/m249.png',
  negev: 'https://stash.clash.gg/img/weapons/512x384/negev.png',
  knife: 'https://stash.clash.gg/img/weapons/512x384/bayonet.png',
  gloves: 'https://static.wikia.nocookie.net/cswikia/images/a/ad/Csgo_T_gloves.png/revision/latest?cb=20161208192318',
};

const WEAPON_VARIANTS = ['usp', 'p2000', 'm4a4', 'm4a1s', 'fiveseven', 'tec9', 'cz75'];
const API_BASE = 'https://repeaked-backend-production.up.railway.app';

function safeJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function mapWeaponId(dbWeaponId) {
  if (!dbWeaponId) return null;
  const id = dbWeaponId.replace(/^weapon_/, '');
  const directMap = {
    m4a1: 'm4a4',
    m4a1_silencer: 'm4a1s',
    hkp2000: 'p2000',
    usp_silencer: 'usp',
    elite: 'dualies',
    fn57: 'fiveseven',
    fiveseven: 'fiveseven',
    tec9: 'tec9',
    cz75a: 'cz75',
    cz75_auto: 'cz75',
    revolver: 'r8',
    mp5sd: 'mp5sd',
    bizon: 'bizon',
    mag7: 'mag7',
    sawedoff: 'sawedoff',
    xm1014: 'xm1014',
    ump45: 'ump45',
    mac10: 'mac10',
    galilar: 'galil',
  };

  if (directMap[id]) return directMap[id];
  if (
    id.includes('knife') || id.includes('bayonet') || id.includes('karambit') ||
    id.includes('butterfly') || id.includes('falchion') || id.includes('flip') ||
    id.includes('gut') || id.includes('huntsman') || id.includes('m9_bayonet') ||
    id.includes('navaja') || id.includes('stiletto') || id.includes('talon') ||
    id.includes('ursus') || id.includes('bowie') || id.includes('shadow_daggers') ||
    id.includes('classic') || id.includes('paracord') || id.includes('survival') ||
    id.includes('nomad') || id.includes('skeleton') || id.includes('push') ||
    id.includes('cord') || id.includes('canis') || id.includes('outdoor') ||
    id.includes('css')
  ) return 'knife';

  if (
    id.includes('glove') || id.includes('handwrap') || id.includes('sporty') ||
    id.includes('slick') || id.includes('motorcycle') || id.includes('specialist') ||
    id.includes('bloodhound') || id.includes('hydra') || id.includes('brokenfang')
  ) return 'gloves';

  return id;
}

const getRarityColor = (rarity) => RARITY_COLORS[rarity] || C.borderBright;
const getSkinName = (item) => item?.skins?.name || item?.name || 'Unknown Skin';
const getSkinImage = (item) => item?.skins?.image_url || item?.image_url || null;
const getSkinRarity = (item) => item?.skins?.rarity || item?.rarity || 'Mil-Spec';
const hasNametag = (item) => Boolean(item?.nametag && item.nametag.trim());

const useIsMobile = (bp = 900) => {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [bp]);
  return mobile;
};

const useHover = () => {
  const [hover, setHover] = useState(false);
  return [hover, { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) }];
};

const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span aria-hidden="true" style={{
    display: 'inline-block',
    width: size,
    height: size,
    border: `${Math.max(1.5, size / 9)}px solid ${color}`,
    background: hollow ? 'transparent' : color,
    transform: 'rotate(45deg)',
    position: 'relative',
    boxSizing: 'border-box',
    flexShrink: 0,
    ...style,
  }}>
    {withDot && (
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: size * 0.32,
        height: size * 0.32,
        background: dotColor,
        transform: 'translate(-50%, -50%)',
      }} />
    )}
  </span>
);

const Eyebrow = ({ children, color = C.red, style }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: F.body,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 2.5,
    textTransform: 'uppercase',
    color: C.textDim,
    ...style,
  }}>
    <Diamond size={6} color={color} hollow={false} />
    <span>{children}</span>
  </div>
);

const IconDots = ({ color = 'currentColor' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="12" cy="5" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="12" cy="19" r="1.7" />
  </svg>
);

const IconTag = ({ color = 'currentColor', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
    <path d="M4 6h16v12H4z" />
    <path d="M7 10h10M7 14h6" />
  </svg>
);

const IconSteam = ({ color = C.steam, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.624 0 11.998-5.373 11.998-12.001C23.977 5.373 18.603 0 11.979 0z" />
  </svg>
);

const FragzIcon = ({ size = 24 }) => (
  <img src="/assets/fragz.png" alt="Fragz" style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
);

const DriftField = ({ count = 9, fixed = false }) => {
  const [diamonds, setDiamonds] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 24 + Math.random() * 70,
      rot: Math.random() * 360,
      vx: (Math.random() - 0.5) * 0.04,
      vy: (Math.random() - 0.5) * 0.03 - 0.005,
      vrot: (Math.random() - 0.5) * 0.15,
      opacity: 0.04 + Math.random() * 0.08,
      color: Math.random() > 0.8 ? C.orange : C.red,
    }))
  );

  useEffect(() => {
    let raf;
    let lastTick = performance.now();
    const tick = (now) => {
      const dt = Math.min(50, now - lastTick);
      lastTick = now;
      const step = dt / 16.67;
      setDiamonds((prev) =>
        prev.map((d) => {
          let nx = d.x + d.vx * step;
          let ny = d.y + d.vy * step;
          if (nx > 110) nx = -10;
          if (nx < -10) nx = 110;
          if (ny > 110) ny = -10;
          if (ny < -10) ny = 110;
          return { ...d, x: nx, y: ny, rot: d.rot + d.vrot * step };
        })
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: fixed ? 'fixed' : 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: fixed ? 1 : 0,
      }}
    >
      {diamonds.map((d) => (
        <span
          key={d.id}
          style={{
            position: 'absolute',
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            border: `1.5px solid ${d.color}`,
            opacity: d.opacity,
            transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
};

const Grain = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 9999,
      opacity: 0.05,
      mixBlendMode: 'overlay',
      backgroundImage:
        'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
      backgroundSize: '160px 160px',
    }}
  />
);

const PanelStat = ({ value, label, accent = C.red, children }) => (
  <div style={{
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${accent}`,
    padding: '11px 14px',
    minWidth: 118,
    minHeight: 68,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: F.display, fontSize: 24, letterSpacing: 1, lineHeight: 1, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
      {children}
      {value}
    </div>
    <div style={{ marginTop: 4, fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 1.7, textTransform: 'uppercase', color: C.textDim }}>
      {label}
    </div>
  </div>
);

const ActionButton = ({ children, onClick, disabled, variant = 'primary', style, type = 'button' }) => {
  const [hover, hp] = useHover();
  const primary = variant === 'primary';
  const danger = variant === 'danger';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      {...hp}
      style={{
        minHeight: 42,
        padding: '11px 18px',
        borderTop: primary ? 'none' : `1.5px solid ${danger ? C.red : hover ? C.borderBright : C.border}`,
        borderRight: primary ? 'none' : `1.5px solid ${danger ? C.red : hover ? C.borderBright : C.border}`,
        borderBottom: primary ? 'none' : `1.5px solid ${danger ? C.red : hover ? C.borderBright : C.border}`,
        borderLeft: primary ? 'none' : `1.5px solid ${danger ? C.red : hover ? C.borderBright : C.border}`,
        background: primary
          ? (disabled ? C.redDim : `linear-gradient(135deg, ${C.red}, ${C.orange})`)
          : (hover && !disabled ? 'rgba(255,255,255,0.04)' : 'transparent'),
        color: danger ? C.red : primary ? C.text : hover ? C.text : C.textDim,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: F.display,
        fontSize: 14,
        letterSpacing: 2,
        textTransform: 'uppercase',
        transform: hover && !disabled ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: primary && hover && !disabled ? `0 10px 26px -12px ${C.redGlow}` : 'none',
        transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
        opacity: disabled ? 0.65 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const DetailBackButton = ({ onClick, label = 'Back' }) => {
  const [hover, hp] = useHover();
  return (
    <button
      type="button"
      onClick={onClick}
      {...hp}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: 0,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: F.body,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 2.2,
        textTransform: 'uppercase',
        color: hover ? C.text : C.textDim,
        transition: 'color 160ms ease',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          color: hover ? C.red : C.textVeryDim,
          transform: hover ? 'translateX(-2px)' : 'translateX(0)',
          transition: 'color 160ms ease, transform 160ms ease',
        }}
      >
        ←
      </span>
      {label}
    </button>
  );
};

const WeaponArt = ({ weaponId, skin, muted = false, style }) => {
  const src = skin?.image_url || DEFAULT_WEAPON_IMAGES[weaponId];
  const alt = skin?.name || WEAPONS.find((w) => w.id === weaponId)?.name || 'Weapon';
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', ...style }}>
        <Diamond size={40} color={muted ? C.borderBright : C.red} withDot dotColor={C.orange} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setError(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        opacity: muted ? 0.22 : 1,
        filter: muted ? 'grayscale(100%)' : 'drop-shadow(0 18px 24px rgba(0,0,0,0.55))',
        ...style,
      }}
    />
  );
};

const ModalShell = ({ children, onClose, accent = C.red, maxWidth = 430 }) => {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={st.modalOverlay}>
      <div onClick={(event) => event.stopPropagation()} style={{ ...st.modalBox, maxWidth, borderLeftColor: accent }}>
        <button type="button" onClick={onClose} aria-label="Close dialog" style={st.modalClose}>x</button>
        <Diamond size={170} color={accent} style={{ position: 'absolute', right: -74, top: -72, opacity: 0.06, pointerEvents: 'none' }} />
        {children}
      </div>
    </div>
  );
};

const EmptyState = ({ title, copy, action }) => (
  <div style={st.emptyState}>
    <Diamond size={46} color={C.borderBright} withDot dotColor={C.red} />
    <div style={st.emptyTitle}>{title}</div>
    <p style={st.emptyCopy}>{copy}</p>
    {action}
  </div>
);

const WeaponCard = ({ weapon, equipped, count, inactive, onClick }) => {
  const [hover, hp] = useHover();
  const rarityColor = equipped ? getRarityColor(equipped.rarity) : C.borderBright;
  const activeAccent = inactive ? C.textVeryDim : equipped ? rarityColor : C.borderBright;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${weapon.name} skins`}
      {...hp}
      style={{
        ...st.weaponCard,
        opacity: inactive ? 0.42 : 1,
        background: hover ? C.cardAlt : C.card,
        borderTopColor: hover ? C.borderBright : C.border,
        borderRightColor: hover ? C.borderBright : C.border,
        borderBottomColor: equipped ? `${rarityColor}88` : hover ? C.borderBright : C.border,
        borderLeftColor: activeAccent,
        transform: hover && !inactive ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hover && !inactive ? `0 16px 36px rgba(0,0,0,0.35), 0 0 34px ${rarityColor}12` : 'none',
      }}
    >
      <div style={st.weaponTopline}>
        <span style={{ ...st.weaponCategory, color: inactive ? C.textVeryDim : C.textDim }}>{weapon.category}</span>
        {WEAPON_VARIANTS.includes(weapon.id) && (
          <span style={{ ...st.statusPill, color: inactive ? C.textVeryDim : C.green, borderColor: inactive ? C.border : `${C.green}66` }}>
            {inactive ? 'Inactive' : 'ON'}
          </span>
        )}
      </div>
      <div style={st.weaponImagePanel}>
        <WeaponArt weaponId={weapon.id} skin={equipped} muted={!equipped} />
      </div>
      <div style={st.weaponName}>{weapon.name}</div>
      <div style={{ ...st.weaponSkinName, color: equipped ? rarityColor : C.textVeryDim }}>
        {equipped?.name || 'Default issue'}
      </div>
      <div style={st.weaponFooter}>
        <span>{count} owned</span>
        <span style={{ color: equipped ? rarityColor : C.textVeryDim }}>{equipped ? 'equipped' : 'empty'}</span>
      </div>
    </button>
  );
};

const SkinCard = ({
  skin,
  selectedWeapon,
  isEquipped,
  menuOpen,
  onMenu,
  menuRef,
  onEquip,
  onApplyStatTrak,
  onApplyNametag,
  onRemoveStatTrak,
}) => {
  const [hover, hp] = useHover();
  const rarity = getSkinRarity(skin);
  const rarityColor = getRarityColor(rarity);
  const stattrak = Boolean(skin.stattrak);
  const nametag = hasNametag(skin);
  const isGlove = skin.skins?.type === 'glove' || mapWeaponId(skin.weapon_id) === 'gloves';

  return (
    <article
      {...hp}
      style={{
        ...st.skinCard,
        background: hover ? C.cardAlt : C.card,
        borderTopColor: hover || isEquipped ? `${rarityColor}88` : C.border,
        borderRightColor: hover || isEquipped ? `${rarityColor}88` : C.border,
        borderBottomColor: hover || isEquipped ? `${rarityColor}88` : C.border,
        borderLeftColor: isEquipped ? rarityColor : hover ? `${rarityColor}cc` : C.borderBright,
        boxShadow: isEquipped ? `0 0 38px ${rarityColor}18` : hover ? `0 16px 36px rgba(0,0,0,0.35), 0 0 28px ${rarityColor}14` : 'none',
        transform: hover ? 'translateY(-3px)' : 'translateY(0)',
      }}
    >
      <div style={st.skinMetaRow}>
        <span style={{ ...st.statusPill, color: rarityColor, borderColor: `${rarityColor}66` }}>{rarity}</span>
        {isEquipped && <span style={{ ...st.statusPill, color: C.text, borderColor: `${C.red}88`, background: 'rgba(232,25,44,0.14)' }}>Equipped</span>}
      </div>

      <button
        type="button"
        aria-label={`Open actions for ${getSkinName(skin)}`}
        onClick={(event) => {
          event.stopPropagation();
          onMenu();
        }}
        style={{ ...st.iconButton, opacity: hover || menuOpen ? 1 : 0.3 }}
      >
        <IconDots />
      </button>

      {menuOpen && (
        <div ref={menuRef} style={st.actionMenu}>
          {!isGlove && !stattrak && (
            <button type="button" style={st.actionMenuItem} onClick={onApplyStatTrak}>
              <span style={{ ...st.actionMenuGlyph, color: C.orange }}>ST</span>
              Apply StatTrak
            </button>
          )}
          <button type="button" style={st.actionMenuItem} onClick={onApplyNametag}>
            <span style={st.actionMenuGlyph}><IconTag size={15} color={C.textDim} /></span>
            {nametag ? 'Change Nametag' : 'Apply Nametag'}
          </button>
          {stattrak && !isGlove && (
            <button type="button" style={{ ...st.actionMenuItem, color: C.red }} onClick={onRemoveStatTrak}>
              <span style={{ ...st.actionMenuGlyph, color: C.red }}>x</span>
              Remove StatTrak
            </button>
          )}
        </div>
      )}

      <div style={{ ...st.skinImagePanel, background: `radial-gradient(circle at 50% 20%, ${rarityColor}22, transparent 52%), linear-gradient(180deg, ${C.bgWarmer}, #070504)` }}>
        {getSkinImage(skin)
          ? <img src={getSkinImage(skin)} alt={getSkinName(skin)} loading="lazy" style={st.skinImage} />
          : <WeaponArt weaponId={selectedWeapon.id} muted />}
      </div>

      <div style={st.skinBody}>
        <div style={st.skinName}>{getSkinName(skin)}</div>
        {nametag && <div style={st.nametag}>"{skin.nametag}"</div>}
        <div style={st.skinWeapon}>{skin.skins?.weapon || selectedWeapon.name}</div>
        <div style={st.skinBadges}>
          {stattrak && (
            <span style={{ ...st.inlineBadge, color: C.orange, borderColor: `${C.orange}66` }}>
              ST {skin.stattrak_count || 0}
            </span>
          )}
          {nametag && (
            <span style={{ ...st.inlineBadge, color: C.textDim, borderColor: C.borderBright }}>
              Tagged
            </span>
          )}
        </div>
      </div>

      <ActionButton onClick={onEquip} variant={isEquipped ? 'secondary' : 'primary'} style={{ margin: '0 16px 16px' }}>
        {isEquipped ? 'Unequip' : 'Equip'}
      </ActionButton>
    </article>
  );
};

export default function Inventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(980);
  const isSmall = useIsMobile(640);
  const menuRef = useRef(null);

  const [inventory, setInventory] = useState(() => safeJson('cached_inventory', []));
  const [equippedSkins, setEquippedSkins] = useState(() => safeJson('cached_equipped', {}));
  const [fragz, setFragz] = useState(0);
  const [stattrakKits, setStattrakKits] = useState(0);
  const [nametagKits, setNametagKits] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('RIFLE');
  const [selectedWeapon, setSelectedWeapon] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showSteamPopup, setShowSteamPopup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [stattrakConfirm, setStattrakConfirm] = useState(null);
  const [removeStattrakConfirm, setRemoveStattrakConfirm] = useState(null);
  const [nametagPopup, setNametagPopup] = useState(null);
  const [nametagInput, setNametagInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [ctPistol, setCtPistol] = useState('usp_silencer');
  const [ctRifle, setCtRifle] = useState('m4a1');
  const [ctSecondary, setCtSecondary] = useState('fiveseven');
  const [tSecondary, setTSecondary] = useState('tec9');

  const steamLinked = Boolean(user?.steam_id || safeJson('cached_user', {})?.steam_id);

  const updateInventory = useCallback((updater) => {
    setInventory((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem('cached_inventory', JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const load = async () => {
      try {
        const [meRes, invRes] = await Promise.all([
          getMe().catch(() => null),
          getInventory(user.username).catch(() => null),
        ]);

        if (cancelled) return;

        const stats = meRes?.data?.stats || {};
        setFragz(stats.fragz || 0);
        setStattrakKits(stats.stattrak_kits || 0);
        setNametagKits(stats.nametag_kits || 0);
        setCtPistol(stats.ct_pistol || 'usp_silencer');
        setCtRifle(stats.ct_rifle || 'm4a1');
        setCtSecondary(stats.ct_secondary || 'fiveseven');
        setTSecondary(stats.t_secondary || 'tec9');

        const items = invRes?.data?.inventory || [];
        setInventory(items);
        localStorage.setItem('cached_inventory', JSON.stringify(items));

        const equipped = {};
        items.forEach((item) => {
          if (item.equipped === true) {
            const frontendId = mapWeaponId(item.weapon_id);
            if (frontendId) equipped[frontendId] = item.skins;
          }
        });
        setEquippedSkins(equipped);
        localStorage.setItem('cached_equipped', JSON.stringify(equipped));
      } catch {
        // Keep cached inventory visible if the refresh fails.
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (menuOpen === null) return undefined;
    const handler = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const ownedByWeapon = useMemo(() => {
    const counts = {};
    inventory.forEach((item) => {
      const id = mapWeaponId(item.weapon_id);
      if (!id) return;
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [inventory]);

  const weaponSkins = useMemo(() => {
    if (!selectedWeapon) return [];
    return inventory.filter((item) => mapWeaponId(item.weapon_id) === selectedWeapon.id);
  }, [inventory, selectedWeapon]);

  const equippedForSelected = selectedWeapon ? equippedSkins[selectedWeapon.id] : null;
  const equippedCount = Object.keys(equippedSkins).length;

  const isInactiveVariant = useCallback((weaponId) => {
    if (weaponId === 'usp' && ctPistol !== 'usp_silencer') return true;
    if (weaponId === 'p2000' && ctPistol !== 'hkp2000') return true;
    if (weaponId === 'm4a4' && ctRifle !== 'm4a1') return true;
    if (weaponId === 'm4a1s' && ctRifle !== 'm4a1_silencer') return true;
    if (weaponId === 'fiveseven' && ctSecondary !== 'fiveseven') return true;
    if (weaponId === 'tec9' && tSecondary !== 'tec9') return true;
    if (weaponId === 'cz75' && ctSecondary !== 'cz75a' && tSecondary !== 'cz75a') return true;
    return false;
  }, [ctPistol, ctRifle, ctSecondary, tSecondary]);

  const navCategory = selectedWeapon?.category || selectedCategory;

  const categoryWeapons = useMemo(() => WEAPONS.filter((weapon) => {
    if (weapon.category !== selectedCategory) return false;
    if (activeFilter === 'equipped' && !equippedSkins[weapon.id]) return false;
    if (activeFilter === 'owned' && !(ownedByWeapon[weapon.id] > 0)) return false;
    return true;
  }), [selectedCategory, activeFilter, equippedSkins, ownedByWeapon]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedWeapon(null);
    setMenuOpen(null);
  };

  const handleBackToCategory = () => {
    setSelectedWeapon(null);
    setMenuOpen(null);
  };

  const handleWeaponClick = (weapon) => {
    if (!steamLinked) {
      setShowSteamPopup(true);
      return;
    }
    setSelectedCategory(weapon.category);
    setSelectedWeapon(weapon);
    setMenuOpen(null);
  };

  const handleEquip = async (skin) => {
    if (!selectedWeapon) return;
    const alreadyEquipped = equippedSkins[selectedWeapon.id]?.id === skin.skins?.id;
    try {
      await equipSkin(alreadyEquipped ? null : skin.skin_id, skin.weapon_id);
      setEquippedSkins((prev) => {
        const updated = { ...prev };
        if (alreadyEquipped) delete updated[selectedWeapon.id];
        else updated[selectedWeapon.id] = skin.skins;
        localStorage.setItem('cached_equipped', JSON.stringify(updated));
        return updated;
      });
      updateInventory((prev) => prev.map((item) => {
        if (mapWeaponId(item.weapon_id) !== selectedWeapon.id) return item;
        return { ...item, equipped: !alreadyEquipped && item.skin_id === skin.skin_id };
      }));
    } catch (error) {
      console.error('Failed to equip skin', error);
    }
  };

  const handleWeaponToggle = async (slot, newWeapon) => {
    const previous = slot === 'ct_pistol'
      ? ctPistol
      : slot === 'ct_rifle'
        ? ctRifle
        : slot === 'ct_secondary'
          ? ctSecondary
          : tSecondary;

    if (previous === newWeapon) return;

    if (slot === 'ct_pistol') setCtPistol(newWeapon);
    else if (slot === 'ct_rifle') setCtRifle(newWeapon);
    else if (slot === 'ct_secondary') setCtSecondary(newWeapon);
    else setTSecondary(newWeapon);

    try {
      await setWeaponPreference(slot, newWeapon);
    } catch (error) {
      if (slot === 'ct_pistol') setCtPistol(previous);
      else if (slot === 'ct_rifle') setCtRifle(previous);
      else if (slot === 'ct_secondary') setCtSecondary(previous);
      else setTSecondary(previous);
      alert(error.response?.data?.detail || 'Failed to update weapon preference');
    }
  };

  const handleApplyStatTrak = async () => {
    if (!stattrakConfirm || applying) return;
    setApplying(true);
    try {
      await applyStatTrak(stattrakConfirm.id);
      updateInventory((prev) => prev.map((item) =>
        item.id === stattrakConfirm.id ? { ...item, stattrak: true, stattrak_count: 0 } : item
      ));
      setStattrakKits((prev) => Math.max(0, prev - 1));
      setStattrakConfirm(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to apply StatTrak');
    } finally {
      setApplying(false);
    }
  };

  const handleApplyNametag = async () => {
    const nextName = nametagInput.trim();
    if (!nametagPopup || applying || !nextName) return;
    setApplying(true);
    try {
      await applyNametag(nametagPopup.id, nextName);
      updateInventory((prev) => prev.map((item) =>
        item.id === nametagPopup.id ? { ...item, nametag: nextName } : item
      ));
      setNametagKits((prev) => Math.max(0, prev - 1));
      setNametagPopup(null);
      setNametagInput('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to apply nametag');
    } finally {
      setApplying(false);
    }
  };

  const handleRemoveStatTrak = async () => {
    if (!removeStattrakConfirm || applying) return;
    setApplying(true);
    try {
      await removeStatTrak(removeStattrakConfirm.id);
      updateInventory((prev) => prev.map((item) =>
        item.id === removeStattrakConfirm.id ? { ...item, stattrak: false, stattrak_count: 0 } : item
      ));
      setRemoveStattrakConfirm(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove StatTrak');
    } finally {
      setApplying(false);
    }
  };

  const openNametag = (skin) => {
    if (nametagKits < 1) {
      alert('No Nametag kits available. Buy one from the Shop.');
      return;
    }
    setNametagInput(skin.nametag || '');
    setNametagPopup(skin);
  };

  const openStatTrak = (skin) => {
    if (stattrakKits < 1) {
      alert('No StatTrak kits available. Buy one from the Shop.');
      return;
    }
    setStattrakConfirm(skin);
  };

  const categoryLabel = selectedCategory.charAt(0) + selectedCategory.slice(1).toLowerCase();

  return (
    <div style={st.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&display=swap');
        html, body, #root {
          min-height: 100%;
          height: auto !important;
          background: ${C.bg};
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
        * { box-sizing: border-box; }
        ::selection { background: ${C.red}; color: ${C.text}; }
        button:focus-visible, input:focus-visible { outline: 2px solid ${C.orange}; outline-offset: 3px; }
        @keyframes rpPulse { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.12); } }
        @keyframes rp-page-up { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 1ms !important; transition-duration: 1ms !important; }
        }
        .inventory-skin-scroll {
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .inventory-skin-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div aria-hidden="true" style={st.landingWash} />
      <DriftField count={18} fixed />
      <Grain />

      <main style={{ ...st.content, padding: isSmall ? '18px 14px 54px' : '24px 32px 64px' }}>
        <header style={st.heroShell}>
          <div style={st.heroRow}>
            <div style={st.heroVaultCopy}>
              <Eyebrow>Inventory · Loadout vault</Eyebrow>
              <h1 style={st.heroVaultTitle}>
                Vault<span style={{ color: C.red }}>.</span>
              </h1>
              {!steamLinked && (
                <button type="button" onClick={() => setShowSteamPopup(true)} style={st.steamBanner}>
                  <IconSteam size={16} />
                  <span>Link Steam to equip</span>
                </button>
              )}
            </div>

            <div style={st.heroStatsRow}>
              <PanelStat label="Balance" value={fragz.toLocaleString()} accent={C.orange}>
                <FragzIcon size={18} />
              </PanelStat>
              <PanelStat label="Owned" value={inventory.length.toLocaleString()} accent={C.red} />
              <PanelStat label="Equipped" value={`${equippedCount}/${WEAPONS.length}`} accent={C.green} />
              <div style={st.kitPanel}>
                <div style={st.kitMiniLabel}>Kits</div>
                <div style={st.kitRow}>
                  <span><b>ST</b> {stattrakKits}</span>
                  <span><IconTag size={14} color={C.orange} /> {nametagKits}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div style={st.splitLayout}>
          <aside style={st.categorySidebar} aria-label="Weapon categories">
            <div aria-hidden="true" style={st.categorySidebarGlow} />
            <Diamond size={92} color={C.red} style={st.categorySidebarWatermark} />

            <div style={st.categorySidebarTop}>
              <Eyebrow color={C.red}>Loadout</Eyebrow>
              <div style={st.categorySidebarHeading}>Class</div>
              <div style={st.categorySidebarDivider}>
                <span style={st.categorySidebarDividerLine} />
                <Diamond size={7} color={C.orange} hollow={false} />
                <span style={st.categorySidebarDividerLine} />
              </div>
              <div style={st.categorySidebarMeta}>{CATEGORIES.length} categories · {WEAPONS.length} slots</div>
            </div>

            <div style={st.categoryNavList}>
              {CATEGORIES.map((category) => {
                const active = navCategory === category;
                const slotCount = WEAPONS.filter((w) => w.category === category).length;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => handleCategorySelect(category)}
                    style={{
                      ...st.categoryNavItem,
                      ...(active ? st.categoryNavItemActive : null),
                    }}
                  >
                    <span>{category.charAt(0) + category.slice(1).toLowerCase()}</span>
                    <span style={{
                      ...st.categoryNavCount,
                      color: active ? C.textDim : C.textVeryDim,
                    }}
                    >
                      {slotCount}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={st.categorySidebarBottom}>
              <div style={st.categorySidebarStat}>
                <span style={st.categorySidebarStatValue}>{equippedCount}</span>
                <span style={st.categorySidebarStatLabel}>Equipped</span>
              </div>
              <div style={st.categorySidebarFooterLine} />
              <div style={st.categorySidebarFootnote}>
                <Diamond size={5} color={C.borderBright} hollow={false} />
                <span>CT/T variants in slot detail</span>
              </div>
            </div>
          </aside>

          <div style={{
            ...st.rightPane,
            ...(selectedWeapon ? st.rightPaneDetail : null),
          }}
          >
            {selectedWeapon ? (
              <div style={st.detailPane}>
                <div style={st.detailHeader}>
                  <div style={{ ...st.detailHero, gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 132px' }}>
                    <div style={st.detailHeroMain}>
                      <DetailBackButton onClick={handleBackToCategory} />
                      <h2 style={st.detailTitle}>
                        {selectedWeapon.name}<span style={{ color: C.red }}>.</span>
                      </h2>
                      {equippedForSelected ? (
                        <span style={{
                          ...st.selectedEquippedPill,
                          borderColor: `${getRarityColor(equippedForSelected.rarity)}88`,
                          color: getRarityColor(equippedForSelected.rarity),
                        }}
                        >
                          Equipped · {equippedForSelected.name}
                        </span>
                      ) : (
                        <span style={st.detailStatusPill}>Default issue</span>
                      )}
                      <p style={st.detailCopy}>
                        {weaponSkins.length} skin{weaponSkins.length === 1 ? '' : 's'} owned
                        {equippedForSelected ? ` · ${equippedForSelected.name} active` : ''}
                      </p>
                    </div>
                    <div style={st.detailWeaponArt}>
                      <WeaponArt weaponId={selectedWeapon.id} skin={equippedForSelected} muted={!equippedForSelected} />
                    </div>
                  </div>

                  {WEAPON_VARIANTS.includes(selectedWeapon.id) && (
                    <VariantSelector
                      selectedWeapon={selectedWeapon}
                      ctPistol={ctPistol}
                      ctRifle={ctRifle}
                      ctSecondary={ctSecondary}
                      tSecondary={tSecondary}
                      onChange={handleWeaponToggle}
                    />
                  )}
                </div>

                <div className="inventory-skin-scroll" style={st.detailSkinScroll}>
                  {weaponSkins.length > 0 ? (
                    <div style={st.skinGrid}>
                      {weaponSkins.map((skin, index) => {
                        const key = skin.id || `${skin.skin_id}-${index}`;
                        const isEquipped = equippedForSelected?.id === skin.skins?.id;
                        return (
                          <SkinCard
                            key={key}
                            skin={skin}
                            selectedWeapon={selectedWeapon}
                            isEquipped={isEquipped}
                            menuOpen={menuOpen === key}
                            menuRef={menuRef}
                            onMenu={() => setMenuOpen(menuOpen === key ? null : key)}
                            onEquip={() => handleEquip(skin)}
                            onApplyStatTrak={() => {
                              setMenuOpen(null);
                              openStatTrak(skin);
                            }}
                            onApplyNametag={() => {
                              setMenuOpen(null);
                              openNametag(skin);
                            }}
                            onRemoveStatTrak={() => {
                              setMenuOpen(null);
                              setRemoveStattrakConfirm(skin);
                            }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState
                      title={`No ${selectedWeapon.name} skins`}
                      copy="This slot is still running default issue. Pick up a skin from the shop when it rotates in."
                      action={<ActionButton onClick={() => navigate('/shop')}>Open shop</ActionButton>}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div style={st.weaponPane}>
                <h2 style={st.rightPaneTitle}>{categoryLabel}</h2>

                <div style={st.filterBar}>
                  <div style={st.filterRow}>
                    {FILTER_OPTIONS.map((option) => {
                      const active = activeFilter === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setActiveFilter(option.id)}
                          style={{
                            ...st.filterChip,
                            ...(active ? st.filterChipActive : null),
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {categoryWeapons.length > 0 ? (
                  <div style={st.weaponGridDesktop}>
                    {categoryWeapons.map((weapon) => (
                      <WeaponCard
                        key={weapon.id}
                        weapon={weapon}
                        equipped={equippedSkins[weapon.id]}
                        count={ownedByWeapon[weapon.id] || 0}
                        inactive={isInactiveVariant(weapon.id)}
                        onClick={() => handleWeaponClick(weapon)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No weapons match"
                    copy={`No ${categoryLabel.toLowerCase()} slots match this filter.`}
                    action={<ActionButton variant="secondary" onClick={() => setActiveFilter('all')}>Show all</ActionButton>}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {showSteamPopup && (
        <ModalShell onClose={() => setShowSteamPopup(false)} accent={C.steam}>
          <div style={st.modalIcon}><IconSteam size={34} /></div>
          <div style={st.modalTitle}>Link Steam</div>
          <p style={st.modalCopy}>
            Link your Steam account to equip skins and have them load automatically when you join a Repeaked server.
          </p>
          <ActionButton
            onClick={() => {
              window.location.href = `${API_BASE}/auth/steam/link`;
            }}
            style={{ width: '100%' }}
          >
            Link Steam account
          </ActionButton>
          <div style={st.modalNote}>Only used to load your skins in-game.</div>
        </ModalShell>
      )}

      {stattrakConfirm && (
        <ModalShell onClose={() => setStattrakConfirm(null)} accent={C.orange}>
          <div style={{ ...st.modalIcon, color: C.orange, fontFamily: F.display, fontSize: 34 }}>ST</div>
          <div style={st.modalTitle}>Apply StatTrak</div>
          <p style={st.modalCopy}>
            Apply StatTrak to <span style={{ color: C.text }}>{getSkinName(stattrakConfirm)}</span>? This starts kill tracking from zero.
          </p>
          <div style={st.modalNote}>Kits remaining: {stattrakKits}</div>
          <div style={st.modalActions}>
            <ActionButton variant="secondary" onClick={() => setStattrakConfirm(null)} style={{ flex: 1 }}>Cancel</ActionButton>
            <ActionButton onClick={handleApplyStatTrak} disabled={applying} style={{ flex: 1 }}>
              {applying ? 'Applying...' : 'Apply'}
            </ActionButton>
          </div>
        </ModalShell>
      )}

      {nametagPopup && (
        <ModalShell
          onClose={() => {
            setNametagPopup(null);
            setNametagInput('');
          }}
          accent={C.orange}
        >
          <div style={st.modalIcon}><IconTag size={34} color={C.orange} /></div>
          <div style={st.modalTitle}>Apply Nametag</div>
          <p style={st.modalCopy}>Name your <span style={{ color: C.text }}>{getSkinName(nametagPopup)}</span>.</p>
          <label htmlFor="nametag-input" style={st.inputLabel}>Nametag</label>
          <input
            id="nametag-input"
            value={nametagInput}
            maxLength={20}
            onChange={(event) => setNametagInput(event.target.value)}
            autoFocus
            placeholder="Enter a name"
            style={st.nametagInput}
          />
          <div style={st.inputHint}>{nametagInput.length}/20 characters</div>
          {nametagInput.trim() && <div style={st.previewBox}>Preview: "{nametagInput.trim()}"</div>}
          <div style={st.modalNote}>Kits remaining: {nametagKits}</div>
          <div style={st.modalActions}>
            <ActionButton
              variant="secondary"
              onClick={() => {
                setNametagPopup(null);
                setNametagInput('');
              }}
              style={{ flex: 1 }}
            >
              Cancel
            </ActionButton>
            <ActionButton onClick={handleApplyNametag} disabled={applying || !nametagInput.trim()} style={{ flex: 1 }}>
              {applying ? 'Applying...' : 'Apply'}
            </ActionButton>
          </div>
        </ModalShell>
      )}

      {removeStattrakConfirm && (
        <ModalShell onClose={() => setRemoveStattrakConfirm(null)} accent={C.red}>
          <div style={{ ...st.modalIcon, color: C.red, fontFamily: F.display, fontSize: 34 }}>!</div>
          <div style={{ ...st.modalTitle, color: C.red }}>Destroy StatTrak</div>
          <p style={st.modalCopy}>
            Removing StatTrak from <span style={{ color: C.text }}>{getSkinName(removeStattrakConfirm)}</span> permanently deletes the module and tracked kills.
          </p>
          <div style={st.warningBox}>
            <span>Tracked kills lost: {removeStattrakConfirm.stattrak_count || 0}</span>
            <span>No Fragz refund is issued.</span>
            <span>A new kit is required to add StatTrak again.</span>
          </div>
          <div style={st.modalActions}>
            <ActionButton variant="secondary" onClick={() => setRemoveStattrakConfirm(null)} style={{ flex: 1 }}>Keep it</ActionButton>
            <ActionButton variant="danger" onClick={handleRemoveStatTrak} disabled={applying} style={{ flex: 1 }}>
              {applying ? 'Removing...' : 'Destroy'}
            </ActionButton>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function VariantSelector({ selectedWeapon, ctPistol, ctRifle, ctSecondary, tSecondary, onChange }) {
  const rows = [];

  if (selectedWeapon.id === 'usp' || selectedWeapon.id === 'p2000') {
    rows.push({
      label: 'CT pistol',
      slot: 'ct_pistol',
      value: ctPistol,
      options: [
        { id: 'usp_silencer', label: 'USP-S' },
        { id: 'hkp2000', label: 'P2000' },
      ],
    });
  }

  if (selectedWeapon.id === 'm4a4' || selectedWeapon.id === 'm4a1s') {
    rows.push({
      label: 'CT rifle',
      slot: 'ct_rifle',
      value: ctRifle,
      options: [
        { id: 'm4a1', label: 'M4A4' },
        { id: 'm4a1_silencer', label: 'M4A1-S' },
      ],
    });
  }

  if (selectedWeapon.id === 'fiveseven' || selectedWeapon.id === 'cz75') {
    rows.push({
      label: 'CT secondary',
      slot: 'ct_secondary',
      value: ctSecondary,
      options: [
        { id: 'fiveseven', label: 'Five-SeveN' },
        { id: 'cz75a', label: 'CZ75' },
      ],
    });
  }

  if (selectedWeapon.id === 'tec9' || selectedWeapon.id === 'cz75') {
    rows.push({
      label: 'T secondary',
      slot: 't_secondary',
      value: tSecondary,
      options: [
        { id: 'tec9', label: 'Tec-9' },
        { id: 'cz75a', label: 'CZ75' },
      ],
    });
  }

  return (
    <div style={st.variantPanel}>
      <Eyebrow color={C.orange}>Weapon choice</Eyebrow>
      <div style={st.variantRows}>
        {rows.map((row) => (
          <div key={row.slot} style={st.variantRow}>
            <span style={st.variantLabel}>{row.label}</span>
            <span style={st.variantButtons}>
              {row.options.map((option) => {
                const active = row.value === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onChange(row.slot, option.id)}
                    style={{
                      ...st.variantButton,
                      background: active ? C.red : 'transparent',
                      color: active ? C.text : C.textDim,
                      borderColor: active ? C.red : C.border,
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const st = {
  root: {
    position: 'relative',
    minHeight: '100vh',
    overflowX: 'hidden',
    color: C.text,
    fontFamily: F.body,
    background: C.bg,
  },
  landingWash: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 0,
    background: `
      radial-gradient(circle at 20% 15%, rgba(232, 25, 44, 0.12), transparent 50%),
      radial-gradient(circle at 85% 80%, rgba(255, 107, 43, 0.07), transparent 55%),
      linear-gradient(180deg, ${C.bg} 0%, #090705 100%)
    `,
  },
  content: {
    position: 'relative',
    zIndex: 2,
    maxWidth: 1400,
    margin: '0 auto',
    animation: 'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both',
  },
  heroShell: {
    marginBottom: 20,
    padding: '14px 16px',
    background: `linear-gradient(135deg, ${C.card} 0%, ${C.cardAlt} 100%)`,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
  },
  heroRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    flexWrap: 'wrap',
  },
  heroVaultCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flexShrink: 0,
  },
  heroVaultTitle: {
    fontFamily: F.display,
    fontSize: 'clamp(38px, 4vw, 52px)',
    letterSpacing: 2.2,
    lineHeight: 0.92,
    margin: 0,
    color: C.text,
    textTransform: 'uppercase',
  },
  heroStatsRow: {
    display: 'flex',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    gap: 8,
  },
  steamBanner: {
    marginTop: 6,
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 10px',
    background: 'rgba(102,192,244,0.08)',
    borderTop: `1px solid ${C.steam}55`,
    borderRight: `1px solid ${C.steam}55`,
    borderBottom: `1px solid ${C.steam}55`,
    borderLeft: `2px solid ${C.steam}`,
    color: C.text,
    cursor: 'pointer',
    fontFamily: F.body,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  kitPanel: {
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.orange}`,
    padding: '11px 14px',
    minHeight: 68,
  },
  kitMiniLabel: {
    fontFamily: F.body,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 1.8,
    color: C.textDim,
    textTransform: 'uppercase',
  },
  kitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    fontFamily: F.display,
    fontSize: 22,
    letterSpacing: 1.4,
    color: C.text,
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: '220px minmax(0, 1fr)',
    gap: 16,
    alignItems: 'stretch',
    minHeight: 'calc(100vh - 220px)',
  },
  categorySidebar: {
    position: 'sticky',
    top: 16,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 220px)',
    padding: 0,
    overflow: 'hidden',
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  categorySidebarGlow: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `linear-gradient(180deg, rgba(232,25,44,0.12) 0, rgba(232,25,44,0) 22%, rgba(255,107,43,0) 78%, rgba(255,107,43,0.1) 100%)`,
  },
  categorySidebarWatermark: {
    position: 'absolute',
    right: -28,
    top: '38%',
    opacity: 0.08,
    pointerEvents: 'none',
  },
  categorySidebarTop: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '16px 12px 14px',
    borderBottom: `1px solid ${C.border}`,
    background: `linear-gradient(180deg, ${C.bgWarmer} 0%, ${C.cardAlt} 100%)`,
  },
  categorySidebarHeading: {
    fontFamily: F.display,
    fontSize: 26,
    letterSpacing: 2.4,
    lineHeight: 1,
    color: C.text,
    textTransform: 'uppercase',
  },
  categorySidebarDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    marginTop: 2,
  },
  categorySidebarDividerLine: {
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${C.borderBright}, transparent)`,
  },
  categorySidebarMeta: {
    marginTop: 2,
    fontFamily: F.body,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    color: C.textVeryDim,
    textAlign: 'center',
  },
  categoryNavList: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    gap: 4,
    padding: '10px 8px',
    background: C.cardAlt,
  },
  categoryNavItem: {
    width: '100%',
    padding: '10px 12px',
    background: C.card,
    borderStyle: 'solid',
    borderWidth: '1px 1px 1px 2px',
    borderTopColor: C.border,
    borderRightColor: C.border,
    borderBottomColor: C.border,
    borderLeftColor: 'transparent',
    color: C.textDim,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    fontFamily: F.body,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    textTransform: 'uppercase',
    transition: 'background 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
  },
  categoryNavCount: {
    fontFamily: F.display,
    fontSize: 14,
    letterSpacing: 1,
    lineHeight: 1,
  },
  categoryNavItemActive: {
    background: '#1c1012',
    borderTopColor: `${C.red}66`,
    borderRightColor: `${C.red}66`,
    borderBottomColor: `${C.red}66`,
    borderLeftColor: C.red,
    color: C.text,
    boxShadow: `0 0 20px ${C.red}18`,
  },
  categorySidebarBottom: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '14px 12px 16px',
    borderTop: `1px solid ${C.border}`,
    background: `linear-gradient(0deg, ${C.bgWarmer} 0%, ${C.cardAlt} 100%)`,
  },
  categorySidebarStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },
  categorySidebarStatValue: {
    fontFamily: F.display,
    fontSize: 28,
    letterSpacing: 1.5,
    lineHeight: 1,
    color: C.green,
  },
  categorySidebarStatLabel: {
    fontFamily: F.body,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: C.textDim,
  },
  categorySidebarFooterLine: {
    width: '72%',
    height: 1,
    background: `linear-gradient(90deg, transparent, ${C.borderBright}, transparent)`,
  },
  categorySidebarFootnote: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontFamily: F.body,
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: C.textVeryDim,
    textAlign: 'center',
  },
  rightPane: {
    minWidth: 0,
    maxHeight: 'calc(100vh - 220px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '4px 4px 24px 8px',
    background: 'linear-gradient(180deg, rgba(17,17,17,0.5), rgba(12,9,5,0.35))',
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
  },
  rightPaneDetail: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 220px)',
    maxHeight: 'calc(100vh - 220px)',
    overflow: 'hidden',
    paddingBottom: 8,
  },
  weaponPane: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    padding: '8px 8px 0',
  },
  filterBar: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 16,
    paddingTop: 4,
  },
  rightPaneTitle: {
    fontFamily: F.display,
    fontSize: 28,
    letterSpacing: 2,
    lineHeight: 1,
    margin: 0,
    padding: '4px 8px 0',
    color: C.text,
    textTransform: 'uppercase',
  },
  detailPane: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: 10,
    padding: '4px 8px 0',
    overflow: 'hidden',
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  detailSkinScroll: {
    flex: 1,
    minHeight: 0,
    padding: '2px 0 8px',
  },
  detailCopy: {
    maxWidth: 520,
    color: C.textDim,
    fontSize: 12,
    lineHeight: 1.42,
    margin: 0,
    fontWeight: 500,
  },
  subtitle: {
    maxWidth: 520,
    color: C.textDim,
    fontSize: 13,
    lineHeight: 1.42,
    margin: 0,
    letterSpacing: 0.2,
    fontWeight: 500,
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  filterChip: {
    minHeight: 34,
    padding: '7px 14px',
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    color: C.textDim,
    cursor: 'pointer',
    fontFamily: F.body,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease',
  },
  filterChipActive: {
    background: C.red,
    borderTopColor: C.red,
    borderRightColor: C.red,
    borderBottomColor: C.red,
    borderLeftColor: C.red,
    color: C.text,
  },
  weaponGridDesktop: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
    gap: 12,
    padding: '0 8px 8px',
  },
  weaponCard: {
    position: 'relative',
    width: '100%',
    minHeight: 228,
    padding: 14,
    borderStyle: 'solid',
    borderWidth: '1px 1px 1px 2px',
    cursor: 'pointer',
    color: C.text,
    fontFamily: F.body,
    textAlign: 'left',
    overflow: 'hidden',
    transition: 'background 160ms ease, border-color 160ms ease, transform 180ms ease, box-shadow 180ms ease, opacity 160ms ease',
  },
  weaponTopline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    minHeight: 22,
  },
  weaponCategory: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 20,
    padding: '3px 8px',
    borderStyle: 'solid',
    borderWidth: 1,
    fontFamily: F.body,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    background: 'rgba(255,255,255,0.02)',
  },
  weaponImagePanel: {
    height: 96,
    margin: '11px 0 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `radial-gradient(circle at 50% 28%, rgba(255,255,255,0.06), transparent 58%), ${C.bgWarmer}`,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
  },
  weaponName: {
    fontFamily: F.display,
    fontSize: 18,
    letterSpacing: 1.7,
    lineHeight: 1,
    color: C.text,
    textTransform: 'uppercase',
  },
  weaponSkinName: {
    marginTop: 4,
    minHeight: 28,
    fontSize: 12,
    lineHeight: 1.25,
    fontWeight: 700,
    letterSpacing: 0.8,
  },
  weaponFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 9,
    paddingTop: 9,
    borderTop: `1px solid ${C.border}`,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    color: C.textDim,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  },
  paginationLabel: {
    fontFamily: F.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: C.textDim,
    textAlign: 'center',
  },
  detailShell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  detailHero: {
    display: 'grid',
    gap: 16,
    alignItems: 'center',
    background: `linear-gradient(135deg, ${C.card}, ${C.cardAlt})`,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    padding: '16px 18px',
    overflow: 'hidden',
  },
  detailHeroMain: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
  },
  detailTitle: {
    fontFamily: F.display,
    fontSize: 'clamp(34px, 4vw, 50px)',
    letterSpacing: 2,
    lineHeight: 0.9,
    margin: 0,
    textTransform: 'uppercase',
  },
  detailStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 28,
    padding: '5px 9px',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: C.borderBright,
    background: 'rgba(255,255,255,0.03)',
    fontFamily: F.body,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: C.textDim,
  },
  selectedEquippedPill: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    minHeight: 28,
    padding: '5px 9px',
    borderStyle: 'solid',
    borderWidth: 1,
    background: 'rgba(255,255,255,0.03)',
    fontFamily: F.body,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailWeaponArt: {
    minHeight: 102,
    maxHeight: 118,
    background: `radial-gradient(circle at 50% 35%, ${C.red}18, transparent 62%), ${C.bgWarmer}`,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    padding: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  variantPanel: {
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.orange}`,
    padding: 12,
  },
  variantRows: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 9,
  },
  variantRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    padding: 7,
    background: C.cardAlt,
    border: `1px solid ${C.border}`,
  },
  variantLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 2,
    color: C.textDim,
    textTransform: 'uppercase',
  },
  variantButtons: {
    display: 'flex',
    border: `1px solid ${C.border}`,
  },
  variantButton: {
    minHeight: 34,
    padding: '8px 16px',
    borderStyle: 'solid',
    borderWidth: 0,
    borderRightWidth: 1,
    cursor: 'pointer',
    fontFamily: F.display,
    fontSize: 13,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    transition: 'background 160ms ease, color 160ms ease',
  },
  skinGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: 12,
  },
  skinCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 340,
    borderStyle: 'solid',
    borderWidth: '1px 1px 1px 2px',
    overflow: 'visible',
    transition: 'background 160ms ease, border-color 160ms ease, transform 180ms ease, box-shadow 180ms ease',
  },
  skinMetaRow: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    maxWidth: 'calc(100% - 54px)',
  },
  iconButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 5,
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.48)',
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.borderBright}`,
    borderBottom: `1px solid ${C.borderBright}`,
    borderLeft: `1px solid ${C.borderBright}`,
    color: C.textDim,
    cursor: 'pointer',
    transition: 'opacity 160ms ease, color 160ms ease',
  },
  actionMenu: {
    position: 'absolute',
    top: 48,
    right: 10,
    zIndex: 20,
    minWidth: 206,
    background: C.card,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.borderBright}`,
    borderBottom: `1px solid ${C.borderBright}`,
    borderLeft: `2px solid ${C.red}`,
    boxShadow: '0 18px 40px rgba(0,0,0,0.62)',
    overflow: 'hidden',
  },
  actionMenuItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '11px 13px',
    background: 'transparent',
    border: 'none',
    color: C.textDim,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: F.body,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.4,
  },
  actionMenuGlyph: {
    width: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: F.display,
    fontSize: 13,
  },
  skinImagePanel: {
    height: 138,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderBottom: `1px solid ${C.border}`,
    overflow: 'hidden',
  },
  skinImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    filter: 'drop-shadow(0 18px 24px rgba(0,0,0,0.55))',
  },
  skinBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    flex: 1,
    padding: '12px 14px 10px',
  },
  skinName: {
    fontFamily: F.display,
    fontSize: 19,
    lineHeight: 1,
    letterSpacing: 1.4,
    color: C.text,
    textTransform: 'uppercase',
  },
  nametag: {
    color: C.orange,
    fontSize: 12,
    fontStyle: 'italic',
    fontWeight: 700,
    letterSpacing: 0.7,
  },
  skinWeapon: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 2,
    color: C.textDim,
    textTransform: 'uppercase',
  },
  skinBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  inlineBadge: {
    minHeight: 22,
    padding: '4px 8px',
    borderStyle: 'solid',
    borderWidth: 1,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  emptyState: {
    minHeight: 330,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 13,
    textAlign: 'center',
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.borderBright}`,
    padding: 28,
  },
  emptyTitle: {
    fontFamily: F.display,
    fontSize: 30,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: C.text,
  },
  emptyCopy: {
    maxWidth: 430,
    margin: 0,
    color: C.textDim,
    lineHeight: 1.55,
    fontWeight: 500,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    background: 'rgba(12,9,5,0.86)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  modalBox: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    background: C.card,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    padding: 32,
    textAlign: 'center',
    boxShadow: '0 28px 80px rgba(0,0,0,0.62)',
  },
  modalClose: {
    position: 'absolute',
    top: 13,
    right: 13,
    zIndex: 2,
    width: 34,
    height: 34,
    background: 'transparent',
    border: 'none',
    color: C.textDim,
    cursor: 'pointer',
    fontFamily: F.display,
    fontSize: 18,
    textTransform: 'uppercase',
  },
  modalIcon: {
    position: 'relative',
    zIndex: 1,
    width: 64,
    height: 64,
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: C.cardAlt,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.borderBright}`,
  },
  modalTitle: {
    position: 'relative',
    zIndex: 1,
    fontFamily: F.display,
    fontSize: 31,
    letterSpacing: 2.4,
    lineHeight: 1,
    color: C.text,
    textTransform: 'uppercase',
  },
  modalCopy: {
    position: 'relative',
    zIndex: 1,
    color: C.textDim,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.55,
    margin: '12px 0 18px',
  },
  modalNote: {
    position: 'relative',
    zIndex: 1,
    margin: '8px 0 0',
    fontSize: 11,
    color: C.textVeryDim,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
  },
  modalActions: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    gap: 10,
    marginTop: 20,
  },
  inputLabel: {
    display: 'block',
    textAlign: 'left',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 2,
    color: C.textDim,
    textTransform: 'uppercase',
    margin: '12px 0 7px',
  },
  nametagInput: {
    width: '100%',
    minHeight: 46,
    padding: '12px 14px',
    background: C.cardAlt,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.borderBright}`,
    color: C.text,
    outline: 'none',
    fontFamily: F.body,
    fontSize: 16,
    fontWeight: 600,
  },
  inputHint: {
    marginTop: 7,
    textAlign: 'right',
    fontSize: 11,
    color: C.textVeryDim,
    fontWeight: 700,
  },
  previewBox: {
    marginTop: 12,
    padding: '10px 12px',
    background: 'rgba(255,107,43,0.06)',
    border: `1px solid ${C.orange}33`,
    color: C.orange,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  warningBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    marginTop: 14,
    padding: '13px 15px',
    background: 'rgba(232,25,44,0.08)',
    borderTop: `1px solid ${C.red}44`,
    borderRight: `1px solid ${C.red}44`,
    borderBottom: `1px solid ${C.red}44`,
    borderLeft: `2px solid ${C.red}`,
    color: C.textDim,
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.35,
  },
}; 
